import { useState, useRef } from 'react';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { PlaceInput } from '../RouteInputs/PlaceInput';
import {
  buildDirectionalVias, generateLoopViaPoints, pickSeedBearing, overlapScore,
  haversineKm, fanDestinations, interestScore, bearingBetween, destinationPoint,
} from '../../utils/geomath';
import { fetchTerrainPoints, fetchPlacesNearPoints } from '../../utils/overpass';
import { fetchRoute, fetchIsoline, fetchMatrix } from '../../utils/routing';
import { snapPointsToRoads, snapPointsToMainRoads } from '../../utils/snapRoad';
import { computeCurviness } from '../../utils/curviness';
import { panToPoint, fitToGeometry } from '../../utils/mapController';
import { formatDistance, formatDuration } from '../../utils/formatters';
import './RouteBuilder.css';

// Loop generation is parked (slow on free infra — revisit with Kurviger).
// The open "destination options" generator below replaces it for now.
const LOOP_ENABLED = false;

// Loop via-point strategy (only used when LOOP_ENABLED): pentagon snapped to
// main roads (false) vs POI-chained directional vias (true).
const USE_DIRECTIONAL_VIAS = false;

const DEST_CANDIDATES = 5; // destinations fanned out per generation

// Geoapify's isodistance range cap depends on the plan (see
// https://www.geoapify.com/pricing/): Free 10km, API 10-25 20km, API 50-250
// 60km, Custom 200km. Keep this matched to whichever plan is actually active
// — bump it the moment the plan is upgraded — and in sync with
// GEOAPIFY_ISOLINE_MAX_DISTANCE_M in functions/index.js. Below this, Generate
// Loop skips the isoline call entirely (it would just fail) and falls back to
// the geometric fan straight away, instead of wasting a round trip finding
// that out the hard way.
const GEOAPIFY_ISOLINE_MAX_KM = 10;

// After snapping, two vias can land on the same road close together, which
// makes the router drive in and straight back out (retracing). Drop vias that
// fall within minKm of one already kept; keep ≥2 so the loop still has shape.
function dedupeNearbyVias(set, minKm) {
  const out = [];
  for (const v of set) {
    if (out.every(u => haversineKm(u.lat, u.lng, v.lat, v.lng) >= minKm)) out.push(v);
  }
  return out.length >= 2 ? out : set;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const compassName = (deg) => COMPASS[Math.round(((deg % 360) + 360) % 360 / 45) % 8];

// Pick `count` points off a reachable-area boundary, spread across bearings
// (full circle, or a forward arc when extending a route). Each chosen point is
// the boundary vertex closest to a target compass heading → reachable seeds.
function sampleBoundaryByBearing(boundary, fromLat, fromLng, count, baseBearing, spreadDeg) {
  const pts = boundary.map(([lng, lat]) => ({ lat, lng, brg: bearingBetween(fromLat, fromLng, lat, lng) }));
  if (!pts.length) return [];
  const full = spreadDeg >= 360;
  const step = full ? 360 / count : spreadDeg / Math.max(1, count - 1);
  const start = full ? (baseBearing || 0) : baseBearing - spreadDeg / 2;
  const used = new Set();
  const out = [];
  for (let i = 0; i < count; i++) {
    const target = (((start + i * step) % 360) + 360) % 360;
    let best = -1, bestDiff = Infinity;
    for (let j = 0; j < pts.length; j++) {
      if (used.has(j)) continue;
      const diff = Math.abs(((pts[j].brg - target + 540) % 360) - 180);
      if (diff < bestDiff) { bestDiff = diff; best = j; }
    }
    if (best >= 0) { used.add(best); out.push({ lat: pts[best].lat, lng: pts[best].lng, bearing: pts[best].brg }); }
  }
  return out;
}

const VARIANT_COLORS = ['#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#0ea5e9'];

// Curviness drives via-point density (pure geometry). More vias for the same
// distance pack the loop tighter → the router weaves through more connector
// roads → higher measured curviness. Fewer → straighter, direct arterials.
const CURVINESS = [
  { id: 'flowing', label: 'Flowing',     icon: '〜', vias: 3 },
  { id: 'twisty',  label: 'Twisty',      icon: '∿', vias: 4 },
  { id: 'extra',   label: 'Very Twisty', icon: '⌇', vias: 6 },
];

export function RouteBuilder() {
  const { state, dispatch, showToast } = useRoute();
  const { isSubscribed } = useAuth();
  const { waypoints } = state.current;
  const { profile, avoidOptions } = state;

  const baseWps = waypoints.filter(wp => !wp.isLoopVia && !wp.isLoopEnd);
  const loopVias = waypoints.filter(wp => wp.isLoopVia);
  const hasLoop = loopVias.length > 0;

  const startWp = baseWps[0] ?? null;
  const stopWps = baseWps.slice(1);

  const [hasPendingStop, setHasPendingStop] = useState(false);
  const [loopOpen, setLoopOpen] = useState(false);
  const [distKm, setDistKm] = useState(10);
  const [distUnit, setDistUnit] = useState('km');
  const [curviness, setCurviness] = useState('twisty');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [showVariants, setShowVariants] = useState(true);
  const abortRef = useRef(null);

  const handleStartSelect = (wp) => {
    dispatch({ type: 'SET_START', payload: wp });
    if (hasLoop) dispatch({ type: 'CLEAR_LOOP' });
  };
  const handleStartClear = () => {
    const newBase = baseWps.length ? [{ lat: null, lng: null, name: '' }, ...stopWps] : [];
    dispatch({ type: 'SET_BASE_WAYPOINTS', payload: newBase });
    if (hasLoop) dispatch({ type: 'CLEAR_LOOP' });
  };

  const handleStopSelect = (stopIndex, wp) => {
    const newBase = [...baseWps];
    newBase[stopIndex + 1] = wp;
    dispatch({ type: 'SET_BASE_WAYPOINTS', payload: newBase });
    if (hasLoop) dispatch({ type: 'CLEAR_LOOP' });
  };
  const handleStopClear = (stopIndex) => {
    const newBase = [...baseWps];
    newBase[stopIndex + 1] = { lat: null, lng: null, name: '' };
    dispatch({ type: 'SET_BASE_WAYPOINTS', payload: newBase });
    if (hasLoop) dispatch({ type: 'CLEAR_LOOP' });
  };
  const handleRemoveStop = (stopIndex) => {
    const newBase = baseWps.filter((_, i) => i !== stopIndex + 1);
    dispatch({ type: 'SET_BASE_WAYPOINTS', payload: newBase });
    if (hasLoop) dispatch({ type: 'CLEAR_LOOP' });
  };
  const handlePendingSelect = (wp) => {
    dispatch({ type: 'ADD_WAYPOINT', payload: wp });
    setHasPendingStop(false);
    if (hasLoop) dispatch({ type: 'CLEAR_LOOP' });
  };

  const displayDist = distUnit === 'mi' ? Math.round(distKm / 1.60934) : Math.round(distKm);
  const canGenerate = !!(startWp?.lat);

  const generateLoop = async () => {
    const from = baseWps[baseWps.length - 1];
    if (!from?.lat || !startWp?.lat) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsGenerating(true);

    const validBase = baseWps.filter(wp => wp.lat != null && wp.lng != null);
    const viaCount = (CURVINESS.find(c => c.id === curviness) || CURVINESS[1]).vias;

    let viaSets;
    if (USE_DIRECTIONAL_VIAS) {
      // POI-chained vias (parked path) — fetch places, chain both directions.
      let places = [];
      try {
        places = await fetchTerrainPoints(from.lat, from.lng, distKm * 0.8, 'anywhere', controller.signal);
      } catch (err) {
        if (err.name === 'AbortError') { setIsGenerating(false); return; }
      }
      const seed = pickSeedBearing(places, from.lat, from.lng, distKm, viaCount);
      const configs = showVariants
        ? [
            { initialBearing: seed, direction: 1 },
            { initialBearing: seed, direction: -1 },
            { initialBearing: (seed + 137) % 360, direction: 1 },
          ]
        : [{ initialBearing: seed, direction: 1 }];
      viaSets = configs.map(cfg =>
        buildDirectionalVias(places, from.lat, from.lng, distKm, viaCount, cfg)
      );
    } else {
      // Geometric polygon — fully local, no Overpass. Variants are rotations.
      const seed = Math.random() * 360;
      const bearings = showVariants
        ? [seed, (seed + 72) % 360, (seed + 144) % 360]
        : [seed];
      viaSets = bearings.map(b =>
        generateLoopViaPoints(from.lat, from.lng, startWp.lat, startWp.lng, distKm, b, viaCount)
      );
    }

    // Straight-line preview instantly so the map is never blank
    dispatch({
      type: 'SET_GEOMETRY',
      payload: {
        geometry: {
          type: 'LineString',
          coordinates: [
            ...validBase.map(w => [w.lng, w.lat]),
            ...viaSets[0].map(v => [v.lng, v.lat]),
            [startWp.lng, startWp.lat],
          ],
        },
        distance: null,
        duration: null,
      },
    });

    // Snap ALL via-points to roads in one batched request, then route every
    // variant in parallel. Pentagon mode snaps to MAIN roads (avoids dead-end
    // spurs); directional mode keeps generic nearest-road snapping.
    let snappedSets = viaSets;
    try {
      const flat = viaSets.flat();
      const snapped = USE_DIRECTIONAL_VIAS
        ? await snapPointsToRoads(flat, controller.signal)
        : await snapPointsToMainRoads(flat, { signal: controller.signal });
      let idx = 0;
      snappedSets = viaSets.map(set => set.map(() => snapped[idx++]));
    } catch (err) {
      if (err.name === 'AbortError') { setIsGenerating(false); return; }
    }

    // Collapse vias that snapped onto (nearly) the same road point.
    const minSepKm = Math.max(0.25, distKm * 0.03);
    snappedSets = snappedSets.map(set => dedupeNearbyVias(set, minSepKm));

    const routePromises = snappedSets.map(set => {
      const allWps = [
        ...validBase,
        ...set.map(v => ({ ...v, isLoopVia: true })),
        { lat: startWp.lat, lng: startWp.lng, name: startWp.name || 'Start', isLoopEnd: true },
      ];
      return fetchRoute(allWps, profile, avoidOptions, controller.signal, isSubscribed);
    });

    let settled;
    try {
      settled = await Promise.allSettled(routePromises);
    } catch {
      settled = [];
    }
    if (controller.signal.aborted) { setIsGenerating(false); return; }

    // Score variants: retracing first, then closeness to requested distance
    const variants = settled
      .map((r, i) => (r.status === 'fulfilled' && r.value ? { result: r.value, i } : null))
      .filter(Boolean)
      .map(({ result, i }, k) => ({
        id:        `v${i}`,
        color:     VARIANT_COLORS[k % VARIANT_COLORS.length],
        viaPoints: snappedSets[i],
        geometry:  result.geometry,
        distance:  result.distance,
        duration:  result.duration,
        overlap:   overlapScore(result.geometry?.coordinates),
      }))
      .sort((a, b) =>
        (a.overlap - b.overlap) ||
        Math.abs(a.distance / 1000 - distKm) - Math.abs(b.distance / 1000 - distKm)
      );

    if (!variants.length) {
      showToast('Could not route a loop here — try a different distance.', 'error');
      dispatch({ type: 'SET_GEOMETRY', payload: { geometry: null, distance: null, duration: null } });
      setIsGenerating(false);
      return;
    }

    const best = variants[0];
    dispatch({ type: 'GENERATE_LOOP', payload: { viaPoints: best.viaPoints, geometry: best.geometry, distance: best.distance, duration: best.duration } });
    if (showVariants && variants.length > 1) {
      dispatch({ type: 'SET_LOOP_VARIANTS', payload: { variants, selectedId: best.id } });
    }

    setIsGenerating(false);
    setLoopOpen(false);
  };

  // Open generator: propose named destinations at the chosen distance, fanned
  // across directions, ranked so the one whose measured curviness best matches
  // the requested level wins. No loop, no heavy main-road snapping → fast.
  const generateRoute = async () => {
    const realBase = baseWps.filter(wp => wp.lat != null && wp.lng != null && !wp.isGenDest);
    const from = realBase[realBase.length - 1];
    if (!from?.lat) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsGenerating(true);

    // Sweep the full compass from a lone start; bias forward when extending.
    let fanOpts = { spreadDeg: 360 };
    if (realBase.length >= 2) {
      const prev = realBase[realBase.length - 2];
      fanOpts = { baseBearing: bearingBetween(prev.lat, prev.lng, from.lat, from.lng), spreadDeg: 200 };
    }
    const baseBearing = fanOpts.baseBearing ?? 0;

    // Candidate targets. Free: geometric fan. Pro: seed from the real reachable
    // boundary (isodistance) so candidates are genuinely ~distKm away by road —
    // a larger pool the Matrix step then narrows.
    let targets = fanDestinations(from.lat, from.lng, distKm, DEST_CANDIDATES, fanOpts);
    if (isSubscribed && distKm <= GEOAPIFY_ISOLINE_MAX_KM) {
      try {
        const boundary = await fetchIsoline(from.lat, from.lng, distKm * 1000, 'drive', controller.signal);
        if (boundary) targets = sampleBoundaryByBearing(boundary, from.lat, from.lng, 10, baseBearing, fanOpts.spreadDeg);
      } catch (err) {
        if (err.name === 'AbortError') { setIsGenerating(false); return; }
      }
    }

    // One light Overpass query for named places near each target point.
    const searchR = Math.round(Math.min(40000, Math.max(3000, distKm * 300)));
    let places = [];
    try {
      places = await fetchPlacesNearPoints(targets, searchR, controller.signal);
    } catch (err) {
      if (err.name === 'AbortError') { setIsGenerating(false); return; }
    }

    // Best unused place near each target; geometric fallback (compass-named).
    const used = new Set();
    const dests = targets.map(t => {
      const pick = places
        .map((p, idx) => ({ idx, p, d: haversineKm(t.lat, t.lng, p.lat, p.lng), interest: interestScore(p) }))
        .filter(x => !used.has(x.idx) && x.d <= searchR / 1000)
        .sort((a, b) => (b.interest - a.interest) || (a.d - b.d))[0];
      if (pick) {
        used.add(pick.idx);
        return { lat: pick.p.lat, lng: pick.p.lng, name: pick.p.name, bearing: t.bearing };
      }
      return { lat: t.lat, lng: t.lng, name: `${compassName(t.bearing)} · ${Math.round(distKm)} km`, bearing: t.bearing };
    });

    // Pro: pre-rank the candidate pool by real road distance in ONE Matrix call,
    // then route only the DEST_CANDIDATES closest to the requested distance.
    let finalDests = dests.slice(0, DEST_CANDIDATES);
    if (isSubscribed && dests.length > DEST_CANDIDATES) {
      try {
        const cells = await fetchMatrix([[from.lng, from.lat]], dests.map(d => [d.lng, d.lat]), 'motorcycle', controller.signal);
        if (cells) {
          const ranked = dests
            .map((d, i) => ({ d, dist: cells[i]?.distance }))
            .filter(x => x.dist != null && x.dist <= distKm * 1000 * 2.5)
            .sort((a, b) => Math.abs(a.dist - distKm * 1000) - Math.abs(b.dist - distKm * 1000))
            .slice(0, DEST_CANDIDATES)
            .map(x => x.d);
          if (ranked.length) finalDests = ranked;
        }
      } catch (err) {
        if (err.name === 'AbortError') { setIsGenerating(false); return; }
      }
    }

    // Route origin → each destination in parallel.
    let settled;
    try {
      settled = await Promise.allSettled(
        finalDests.map(d => fetchRoute([...realBase, { lat: d.lat, lng: d.lng }], profile, avoidOptions, controller.signal, isSubscribed))
      );
    } catch { settled = []; }
    if (controller.signal.aborted) { setIsGenerating(false); return; }

    // Rank by curviness match (measured), then closeness to requested distance.
    const targetLevel = Math.max(0, CURVINESS.findIndex(c => c.id === curviness));
    const variants = settled
      .map((r, i) => (r.status === 'fulfilled' && r.value ? { result: r.value, i } : null))
      .filter(Boolean)
      .map(({ result, i }) => ({
        id:          `d${i}`,
        destination: finalDests[i],
        geometry:    result.geometry,
        distance:    result.distance,
        duration:    result.duration,
        curviness:   computeCurviness(result.geometry),
      }))
      .sort((a, b) => {
        const da = Math.abs((a.curviness?.level ?? 0) - targetLevel);
        const db = Math.abs((b.curviness?.level ?? 0) - targetLevel);
        return da - db || Math.abs(a.distance / 1000 - distKm) - Math.abs(b.distance / 1000 - distKm);
      });

    if (!variants.length) {
      showToast('Could not find routes at that distance — try another.', 'error');
      setIsGenerating(false);
      return;
    }
    variants.forEach((v, k) => { v.color = VARIANT_COLORS[k % VARIANT_COLORS.length]; });

    const best = variants[0];
    dispatch({
      type: 'SET_LOOP_VARIANTS',
      payload: (showVariants && variants.length > 1) ? { kind: 'dest', variants, selectedId: best.id } : null,
    });
    dispatch({ type: 'APPLY_DEST_VARIANT', payload: best });
    fitToGeometry(best.geometry);

    setIsGenerating(false);
    setLoopOpen(false);
  };

  // Route back home: from the current endpoint, propose return legs back to the
  // start. Candidates bow to either side so they take different roads than the
  // outbound; ranked by least round-trip overlap (most distinct return).
  const generateReturn = async () => {
    const valid = waypoints.filter(w => w.lat != null && w.lng != null);
    const outboundGeom = state.current.geometry;
    if (valid.length < 2 || !outboundGeom?.coordinates?.length) return;

    const home = valid[0];
    const from = valid[valid.length - 1];

    // Snapshot the outbound route so every variant builds from the same base.
    const outboundWps = waypoints;
    const outCoords = outboundGeom.coordinates;
    const outDist = state.current.distance || 0;
    const outDur = state.current.duration || 0;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsReturning(true);

    const sep = haversineKm(from.lat, from.lng, home.lat, home.lng);
    const perp = (bearingBetween(from.lat, from.lng, home.lat, home.lng) + 90) % 360;
    const mid = { lat: (from.lat + home.lat) / 2, lng: (from.lng + home.lng) / 2 };
    // Direct + bowed both sides at two depths → distinct return corridors.
    const candidates = [0, sep * 0.3, -sep * 0.3, sep * 0.6, -sep * 0.6].map(off => {
      if (off === 0) return [];
      const pt = destinationPoint(mid.lat, mid.lng, perp, Math.abs(off) * Math.sign(off));
      return [{ lat: pt.lat, lng: pt.lng }];
    });

    let settled;
    try {
      settled = await Promise.allSettled(
        candidates.map(vias => fetchRoute([from, ...vias, home], profile, avoidOptions, controller.signal, isSubscribed))
      );
    } catch { settled = []; }
    if (controller.signal.aborted) { setIsReturning(false); return; }

    const seen = new Set();
    const variants = settled
      .map((r, i) => (r.status === 'fulfilled' && r.value ? { res: r.value, vias: candidates[i], i } : null))
      .filter(Boolean)
      .filter(({ res }) => {           // drop near-duplicate returns
        const key = Math.round(res.distance / 200);
        if (seen.has(key)) return false;
        seen.add(key); return true;
      })
      .map(({ res, vias, i }) => {
        const ret = res.geometry?.coordinates || [];
        const combined = [...outCoords, ...ret.slice(1)];
        return {
          id: `r${i}`,
          vias,
          geometry: res.geometry,                 // return leg (shown dashed)
          distance: res.distance,
          duration: res.duration,
          overlap: overlapScore(combined),        // round-trip retrace
          _payload: {
            waypoints: [
              ...outboundWps,
              ...vias.map(p => ({ lat: p.lat, lng: p.lng, name: 'Via', isLoopVia: true })),
              { lat: home.lat, lng: home.lng, name: home.name || 'Home', isLoopEnd: true },
            ],
            geometry: { type: 'LineString', coordinates: combined },
            distance: outDist + res.distance,
            duration: outDur + res.duration,
          },
        };
      })
      .sort((a, b) => (a.overlap - b.overlap) || (a.distance - b.distance));

    if (!variants.length) {
      showToast('Could not route back home — try editing the route.', 'error');
      setIsReturning(false);
      return;
    }
    variants.forEach((v, k) => { v.id = `r${k}`; v.color = VARIANT_COLORS[k % VARIANT_COLORS.length]; });

    const best = variants[0];
    dispatch({
      type: 'SET_LOOP_VARIANTS',
      payload: variants.length > 1 ? { kind: 'return', variants, selectedId: best.id } : null,
    });
    dispatch({ type: 'APPLY_RETURN_VARIANT', payload: best });
    fitToGeometry(best._payload.geometry);
    setIsReturning(false);
  };

  const validWps = waypoints.filter(w => w.lat != null && w.lng != null);
  // "Already home" = a return leg was applied (isLoopEnd) OR the route simply
  // ends where it starts (manual loop) — detected geometrically so the button
  // reflects what's actually on the map, not just our own flag. Generous
  // radius since "back home" means the same general area, not the exact spot —
  // a stop a few blocks from the start should still count.
  const HOME_RADIUS_KM = 1.5;
  const first = validWps[0], last = validWps[validWps.length - 1];
  const endsAtStart = validWps.length >= 2 &&
    haversineKm(first.lat, first.lng, last.lat, last.lng) < HOME_RADIUS_KM;
  const returnedHome = endsAtStart || waypoints.some(w => w.isLoopEnd);
  // Show the button for any built route; it stays visible (disabled) once we've
  // looped home so the state is obvious. Hidden only during a parked loop-gen.
  const showReturnBtn = validWps.length >= 2 && !!state.current.geometry && (returnedHome || !hasLoop);

  return (
    <div className="route-builder">
      {/* Waypoints card */}
      <div className="route-builder__card">
        <PlaceInput
          placeholder="Starting point"
          value={startWp?.name || ''}
          dotColor="var(--color-success)"
          label="A"
          onBadgeClick={startWp?.lat != null ? () => panToPoint(startWp.lat, startWp.lng) : undefined}
          onSelect={handleStartSelect}
          onClear={handleStartClear}
        />

        {stopWps.map((wp, i) => (
          <div key={`stop-${i}`} className="route-builder__stop-wrap">
            <div className="route-builder__sep" />
            <div className="route-builder__stop-row">
              <PlaceInput
                placeholder={`Stop ${i + 1}`}
                value={wp?.name || ''}
                pending={!!wp?.pending}
                dotColor={!hasLoop && i === stopWps.length - 1 && !hasPendingStop
                  ? 'var(--color-danger)'
                  : 'var(--color-primary)'}
                label={String.fromCharCode(66 + Math.min(i, 24))}
                onBadgeClick={wp?.lat != null ? () => panToPoint(wp.lat, wp.lng) : undefined}
                onSelect={(w) => handleStopSelect(i, w)}
                onClear={() => handleStopClear(i)}
              />
              <button
                className="route-builder__remove"
                onClick={() => handleRemoveStop(i)}
                title="Remove stop"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                  <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        ))}

        {hasPendingStop && (
          <div className="route-builder__stop-wrap">
            <div className="route-builder__sep" />
            <div className="route-builder__stop-row">
              <PlaceInput
                placeholder={`Stop ${stopWps.length + 1}`}
                value=""
                dotColor={hasLoop ? 'var(--color-primary)' : 'var(--color-danger)'}
                label={String.fromCharCode(66 + Math.min(stopWps.length, 24))}
                onSelect={handlePendingSelect}
                onClear={() => {}}
              />
              <button
                className="route-builder__remove"
                onClick={() => setHasPendingStop(false)}
                title="Remove stop"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                  <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loop status badge */}
      {hasLoop && (
        <div className="route-builder__loop-status">
          <span className="route-builder__loop-icon">↻</span>
          <span className="route-builder__loop-label">
            Loop via {loopVias.length} points → {startWp?.name || 'Start'}
          </span>
          <button className="route-builder__loop-remove" onClick={() => dispatch({ type: 'CLEAR_LOOP' })}>
            Remove
          </button>
        </div>
      )}

      {/* Variant options (loop vias or open destinations) */}
      {state.loopVariants?.variants?.length > 1 && (() => {
        const kind = state.loopVariants.kind;
        const applyType = kind === 'dest' ? 'APPLY_DEST_VARIANT'
          : kind === 'return' ? 'APPLY_RETURN_VARIANT'
          : 'APPLY_LOOP_VARIANT';
        const title = kind === 'dest' ? 'Destinations — tap to compare'
          : kind === 'return' ? 'Routes home — tap to compare'
          : 'Loop options — tap to compare';
        return (
          <div className="route-builder__variants">
            <div className="route-builder__variants-title">{title}</div>
            {state.loopVariants.variants.map((v, i) => {
              const selected = v.id === state.loopVariants.selectedId;
              const name = kind === 'dest' ? (v.destination?.name || `Option ${i + 1}`)
                : kind === 'return' ? `Return ${i + 1}`
                : `Option ${i + 1}`;
              return (
                <button
                  key={v.id}
                  className={`route-builder__variant${selected ? ' route-builder__variant--selected' : ''}`}
                  onClick={() => dispatch({ type: applyType, payload: v })}
                >
                  <span className="route-builder__variant-dot" style={{ background: selected ? '#ff7a1a' : v.color }} />
                  <span className="route-builder__variant-name">{name}</span>
                  <span className="route-builder__variant-meta">
                    {formatDistance(v.distance)} · {formatDuration(v.duration)}
                  </span>
                  {/* "no retrace" badge removed — overlapScore is a rough heuristic,
                      not reliable enough to assert as a fact to the user. It's still
                      used internally to rank/sort variants, just not shown as a claim. */}
                  {kind === 'dest' && v.curviness && <span className="route-builder__variant-badge">{v.curviness.label}</span>}
                  {selected && <span className="route-builder__variant-check">✓</span>}
                </button>
              );
            })}
            <button
              className="route-builder__variant-confirm"
              onClick={() => dispatch({ type: 'SET_LOOP_VARIANTS', payload: null })}
            >
              ✓ Confirm route
            </button>
          </div>
        );
      })()}

      {/* Route back home — visible for any built route, disabled once returned */}
      {showReturnBtn && (
        <button
          className="route-builder__return-btn"
          onClick={generateReturn}
          disabled={isReturning || returnedHome}
        >
          {isReturning ? (
            <span className="route-builder__generating">
              <span className="route-builder__gen-spinner" /> Routing home…
            </span>
          ) : returnedHome ? (
            <><span className="route-builder__return-icon">✓</span> Returned home</>
          ) : (<><span className="route-builder__return-icon">↩</span> Route back home</>)}
        </button>
      )}

      {/* Action buttons */}
      <div className="route-builder__btns">
        <button
          className="route-builder__action-btn"
          onClick={() => setHasPendingStop(true)}
          disabled={hasPendingStop}
        >
          + Add Stop
        </button>
        {LOOP_ENABLED && !hasLoop && (
          <button
            className={`route-builder__action-btn route-builder__action-btn--loop${loopOpen ? ' active' : ''}`}
            onClick={() => setLoopOpen(v => !v)}
            disabled={!startWp?.lat}
            title={startWp?.lat ? 'Generate loop route' : 'Set a starting point first'}
          >
            ↻ Loop
          </button>
        )}
        {!LOOP_ENABLED && (
          <button
            className={`route-builder__action-btn route-builder__action-btn--loop${loopOpen ? ' active' : ''}`}
            onClick={() => setLoopOpen(v => !v)}
            disabled={!startWp?.lat}
            title={startWp?.lat ? 'Generate routes to a destination' : 'Set a starting point first'}
          >
            ✦ Generate
          </button>
        )}
      </div>

      {/* Loop panel */}
      {loopOpen && !hasLoop && (
        <div className="route-builder__loop-panel">
          {/* Distance slider */}
          <div className="route-builder__dist-row">
            <input
              type="range"
              className="route-builder__slider"
              min="1" max="150" step="1"
              value={displayDist}
              onChange={e => {
                const v = Number(e.target.value);
                setDistKm(distUnit === 'mi' ? v * 1.60934 : v);
              }}
            />
            <div className="route-builder__dist-controls">
              <input
                type="number"
                className="route-builder__dist-num"
                min="1" max="999"
                value={displayDist}
                onChange={e => {
                  const v = Number(e.target.value);
                  if (v > 0) setDistKm(distUnit === 'mi' ? v * 1.60934 : v);
                }}
              />
              <div className="route-builder__units">
                <button
                  className={`route-builder__unit${distUnit === 'km' ? ' active' : ''}`}
                  onClick={() => setDistUnit('km')}
                >km</button>
                <button
                  className={`route-builder__unit${distUnit === 'mi' ? ' active' : ''}`}
                  onClick={() => setDistUnit('mi')}
                >mi</button>
              </div>
            </div>
          </div>

          <label className="route-builder__variants-toggle">
            <input
              type="checkbox"
              checked={showVariants}
              onChange={e => setShowVariants(e.target.checked)}
            />
            Compare route options
          </label>

          <button
            className="route-builder__generate-btn"
            onClick={LOOP_ENABLED ? generateLoop : generateRoute}
            /* Once the ride already loops back to the start there's nothing
               left to generate — mirror the disabled "Return home" button. */
            disabled={!canGenerate || isGenerating || returnedHome}
            title={returnedHome ? 'Your route already returns home — clear or edit it to generate again' : undefined}
          >
            {isGenerating ? (
              <span className="route-builder__generating">
                <span className="route-builder__gen-spinner" />
                Routing…
              </span>
            ) : (LOOP_ENABLED ? 'Generate Loop' : 'Generate Routes')}
          </button>
        </div>
      )}
    </div>
  );
}
